import React, { useEffect, useState, useMemo } from "react";

import Backgrounds from "../../_partials/Backgrounds/Index";
import TemplateBlock from "../../_partials/TemplateBlock/Index";
import getContentById from "../../../../shared/utils/getContentById";
import animationScript, { animationCleanUp } from "./animation";
import prepareVideoBg from "../../../../shared/functions/prepareVideoBg";

import "./style/style.scss";
import "./style/animation.scss";
import { MatchScoreWidget } from "../../_partials/MatchScoreWidget/Index";
import ProgressBar from "../../../../shared/components/ProgressBar";

import Top5ShotsOnGoal from "./ShotsOnGoalTop5";
import HotZoneGraph from "./HotZoneGraph";
import { useTranslation } from "react-i18next";
import assets from "./assets";

const BACKGROUND_PATH = "./assets/background_1920";
const COLUMNS = 7;
const COL_WIDTH = 100 / COLUMNS;
// const ROW_HEIGHTS = { top: 25, mid: 50, bottom: 25 };
const ROW_HEIGHTS = { z1: 20, z2: 20, z3: 20, z4: 20, z5: 20 };

const Index = ({ animation, data, mode, format, ...props }) => {
  const refs = {
    player: React.createRef(),
    team: React.createRef(),
  };

  const { t } = useTranslation();
  const { template_name, uuid, data: templateData } = data;
  const [shots, setShots] = useState(
    Array(COLUMNS).fill({ z1: 0, z2: 0, z3: 0, z4: 0, z5: 0 }),
  );

  const templateName = template_name ?? "";
  const id = `${templateName}_${uuid}`;
  const backgroundImage = BACKGROUND_PATH;

  const { matchDetails, goalShots, matchInfo, teamRanking } = templateData?.dynamic ?? {};
  const matchStatus = matchDetails?.matchStatus;
  const scores = matchDetails?.scores;

  const fields = templateData?.fields || [];
  const competitionId = matchInfo?.competition?.id;

  const home = matchInfo?.contestant?.[0];
  const away = matchInfo?.contestant?.[1];
  const teamSide = getContentById("selectedSide", fields, "value") ?? "home";
  const teamId = teamSide === "home" ? home?.id : away?.id;
  const calculateShotPositions = (shots) => {
    const shortsObj = Array.from({ length: COLUMNS }, () => ({
      z1: 0,
      z2: 0,
      z3: 0,
      z4: 0,
      z5: 0,
    }));

    const zoneLimits = [
      ROW_HEIGHTS.z1,
      ROW_HEIGHTS.z1 + ROW_HEIGHTS.z2,
      ROW_HEIGHTS.z1 + ROW_HEIGHTS.z2 + ROW_HEIGHTS.z3,
      ROW_HEIGHTS.z1 + ROW_HEIGHTS.z2 + ROW_HEIGHTS.z3 + ROW_HEIGHTS.z4,
      100,
    ];

    shots.forEach(({ x, y }) => {
      let colIndex = Math.floor(x / COL_WIDTH);
      if (teamSide === "away") {
        colIndex = COLUMNS - colIndex - 1;
      }

      let rowKey = "z5";
      if (y <= zoneLimits[0]) rowKey = "z1";
      else if (y <= zoneLimits[1]) rowKey = "z2";
      else if (y <= zoneLimits[2]) rowKey = "z3";
      else if (y <= zoneLimits[3]) rowKey = "z4";

      if (shortsObj[colIndex]) {
        shortsObj[colIndex][rowKey]++;
      }
    });

    if (teamSide === "home") {
      shortsObj[0] = { z1: 0, z2: 0, z3: 0, z4: 0, z5: 0 };
    } else if (teamSide === "away") {
      shortsObj[COLUMNS - 1] = { z1: 0, z2: 0, z3: 0, z4: 0, z5: 0 };
    }
    return shortsObj;
  };

  const shotCoordinates = useMemo(() => {
    return (
      goalShots
        ?.filter((shot) => shot.contestantId === teamId)
        .map(({ destPosX, destPosY }) => ({ x: destPosX, y: destPosY })) ?? []
    );
  }, [goalShots, teamId]);

  useEffect(() => {
    if (!shotCoordinates.length) {
      setShots(Array(COLUMNS).fill({ upper: 0, middle: 0, lower: 0 }));
      return;
    }
    const updatedShots = calculateShotPositions(shotCoordinates);
    setShots(updatedShots);
  }, [shotCoordinates]);

  const prepareAnimation = () => {
    prepareVideoBg({ id });
  };

  useEffect(() => {
    let animationReturn = null;
    if (mode === "animation" || mode === "video") {
      if (animation) {
        prepareAnimation();
        animationReturn = animationScript({
          refs,
          data,
          animation,
          mode,
          id,
          format,
          apiSetup: props?.apiSetup,
          templateName: data?.template_name,
        });
      }
    }

    return function cleanUp() {
      if (animationReturn) {
        animationCleanUp(animationReturn);
      }
    };
  }, [animation, mode]);

  return (
    <div
      id={id}
      className={`T_${templateName} ${animation ? "animation" : ""}  side-${teamSide}`}
    >
      <TemplateBlock>
        <Backgrounds bg1={backgroundImage} loop />

        <div className="wc-content wc-content-v2">
          <ProgressBar height="8px" />
          <div className="wc-bg-content"></div>

          <div className="qatar-logo">
            <img src={assets.insight_zone_logo} />
          </div>

          <MatchScoreWidget
            homeTeam={home}
            awayTeam={away}
            scores={scores}
            matchStatus={matchStatus}
            matchDetails={matchDetails}
            competitionId={competitionId}
            matchInfo={matchInfo}
          />
          <div className={"left-block-side"}>
            <div className="teams-block">
              <Top5ShotsOnGoal teamRanking={teamRanking} competitionId={competitionId} />
            </div>
          </div>

          <div className={"right-block-side"}>
            <div className="main-header green-glow">
              {t("Hot zone", { keyPrefix: "templates" })}
              <div className="subheadline">
                {t("Offensive Actions")}
              </div>
            </div>


            <HotZoneGraph
              data={{
                homeTeam: home,
                awayTeam: away,
                competitionId,
                shots,
                teamSide,
              }}
            />
            <div className={`arrows-right direction-${teamSide === "home" ? "right" : "left"}`}>
              <img src={assets.right_arrow} />
              <img src={assets.right_arrow} />
              <img src={assets.right_arrow} />
            </div>
          </div>
        </div>
      </TemplateBlock>
    </div>
  );
};

export default Index;
